// The PoE cycle's confirmation (ADR-0251, ADR-0252, ADR-0300): before power
// is cut the panel reads which port the server would cut and how it found it,
// and the cut it sends carries that port back, so it lands on the port shown
// or is refused with nothing sent (decision 5b). The port is always the
// server's: the page never derives one.
//
// **Where the server finds no port, a person may name one** (decision 1b) —
// offered on the refusal whose `poe_refusal` is `no_neighbour` and on no
// other, read from the Problem's field and never from its sentence. The named
// port is previewed first, so what that port last reported is on screen before
// the button that cuts it, and the server refuses a port the switch reports
// another device on (4.2a). The server alone decides who may name one (3b);
// this page is a person's, so it offers it.
//
// **A port is named by picking it on its switch's drawing** (#1778): each
// switch at the access point's site is drawn with the faceplate its catalogue
// model has, the one the device page draws, and a press previews that port as
// named — the same `switch_serial` and `Ethernet<N>` the typed form sends, so
// nothing the API is sent differs from typing it. The console numbers a port
// `1/<N+1>`, and a person who read that number off the switch and typed it
// named the neighbouring port (Hotel Les Cigales, 2026-10-14). A switch whose
// model has no faceplate keeps the typed field, and so does a site whose
// switches could not be listed.
//
// **A port is said the way the console says it first** (`cli_port`, `1/<n>`),
// with `Ethernet<N>` beside it, wherever the panel names one, the button
// included: the console's name is the one a person checks at the switch.
import { useState, type FormEvent } from "react";
import { api } from "../../api/client";
import { SKIP, useDrained, useQuery } from "../../api/hooks";
import type { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { plural } from "../../dashboard/format";
import { Loading, Mac, ProblemBox, When } from "../../ui";
import { ACTIONS, sendPoeCycle, type Device, type Notice } from "./commands";
import DeviceFace, { type PortState } from "./DeviceFace";
import { useDeviceModel } from "./DeviceDetail";
import { facePorts, photoOf } from "./Ports";
import { unrecognized } from "../../api/openEnum";

type Preview = components["schemas"]["PoeCyclePreview"];
type PortRef = components["schemas"]["PoeCyclePortRef"];

// Restates ADR-0300 decisions 1d, 1b and 5b — how the server found the port.
// `fdb` is learned, not announced, and must not be called the access point's
// neighbour; `named` is a person's word and the switch's reports did not find
// it.
function howFound(p: Preview): string {
  switch (p.via) {
    case "fdb": return `learned by the switch${p.vlan ? ` on VLAN ${p.vlan}` : ""}; the access point did not announce itself`;
    case "named": return "named by you; the switch's reports did not find it";
    case "lldp": return "announced by the access point (LLDP)";
    // Restates ADR-0511 decision 5a — a route this build does not know is named as received.
    default: return `found by a route this page does not recognize (${unrecognized(p.via)})`;
  }
}

/** The port as the console names it, then as Zenith does. */
const portWords = (p: { cli_port: string; port: string }) => `${p.cli_port} (${p.port})`;

export default function PoeCyclePanel({ device, m, onDone, onCancel }: {
  device: Device;
  /** The page's one write lane (#189). */
  m: ReturnType<typeof useMutation>;
  onDone(notice: Notice): void;
  onCancel(): void;
}) {
  // The port a person has asked to see, once they ask; the preview re-reads
  // with it. Undefined is the server's own resolution.
  const [named, setNamed] = useState<PortRef>();
  const preview = useQuery<Preview>(() => api.GET("/devices/{serial}/poe-cycle", { params: {
    path: { serial: device.serial },
    query: named ? { switch_serial: named.switch_serial, port: named.port } : undefined,
  } }), [device.serial, named?.switch_serial, named?.port]);
  const shown = preview.status === "ready" ? preview.data : undefined;
  const refusal = preview.status === "error" ? preview.problem : undefined;
  // The one refusal on which naming a port is offered (ADR-0300 1b), and the
  // pick stays offered while a named port is being looked at.
  const mayName = named !== undefined || refusal?.poe_refusal === "no_neighbour";
  // The site's switches: what a port is picked on, and the name a found port's
  // switch is said by. Read once, whole.
  const siteId = device.site_id;
  const switches = useDrained((page_token?: string) => siteId
    ? api.GET("/sites/{id}/devices", { params: { path: { id: siteId }, query: { category: "switch", page_size: 200, ...(page_token ? { page_token } : {}) } } })
    : SKIP, [siteId]);
  const atSite = switches.status === "ready" ? switches.data.items.filter((d) => d.category === "switch") : undefined;
  const switchName = (serial: string) => atSite?.find((d) => d.serial === serial)?.name;
  const go = async () => {
    if (!shown) return;
    const expected = { switch_serial: shown.switch_serial, port: shown.port };
    const notice = await sendPoeCycle(m, device, { expected, named: shown.via === "named" ? expected : undefined });
    if (notice) onDone(notice);
    // A refused cut — the resolution moved, the switch went offline — sent
    // nothing; the port shown is read again so the next press is on a fresh one.
    else preview.reload();
  };
  const a = ACTIONS.poe_cycle;
  const last = shown?.last_reported;
  const swName = shown ? switchName(shown.switch_serial) : undefined;
  return (
    <div className="panel"><h2>{a.title}: {device.name}</h2>
      <p className="muted">{a.explain}</p>
      {preview.status === "loading" && <Loading label="Finding the port" />}
      {shown && (
        <dl className="kv">
          <dt>Port</dt><dd><code>{shown.cli_port}</code> <span className="muted">({shown.port})</span> on {swName ? <>{swName} <code>{shown.switch_serial}</code></> : <>switch <code>{shown.switch_serial}</code></>}</dd>
          <dt>How it was found</dt><dd>{howFound(shown)}{shown.via !== "named" && <>, {shown.reports_back === 0 ? "newest report" : `${shown.reports_back} ${plural(shown.reports_back, "report")} back`}, written <When value={shown.reported_at} /> by the switch&rsquo;s clock</>}</dd>
        </dl>
      )}
      {/* Restates ADR-0300 decision 1b — what a named port last reported is the
          switch's report over its recent window, shown before the cut, and says
          nothing of what is on the port now. It must not say the port is free:
          an empty table is not an empty port. */}
      {last && (
        <div className="muted">
          <p>Last reported on this port (not what is on it now):</p>
          <ul>
            <li>{last.neighbour_mac
              ? <>LLDP neighbor <Mac station={last.neighbour_mac} />, {last.neighbour_reports_back} {plural(last.neighbour_reports_back ?? 0, "report")} back</>
              : "No LLDP neighbor in any recent report."}</li>
            <li>{!last.learned_read ? "No forwarding table could be read."
              : last.learned.length === 0 ? "No address in the newest forwarding table."
              : <>Learned: {last.learned.map((l, i) => <span key={l.mac}>{i > 0 && ", "}<Mac station={l.mac} />{l.name && ` (${l.name})`}</span>)}</>}</li>
          </ul>
        </div>
      )}
      {refusal && <ProblemBox problem={refusal} />}
      {mayName && (
        <div>
          {/* Restates ADR-0300 decisions 1b and 4.2a — a person may name the port when the
              switch's reports find none, sees what it last reported before anything is cut, and a
              port the switch reports another device on is refused. */}
          <p className="muted">Press its port on its switch below. You see what that port last reported before anything is cut, and a port the switch reports another device on is refused.</p>
          {switches.status === "loading" && <Loading label="Reading the site's switches" />}
          {atSite?.map((sw) => <SwitchPick key={sw.serial} sw={sw} named={named} onPick={setNamed} />)}
          {switches.status === "error" && <p className="muted">The site&rsquo;s switches could not be listed, so type the switch and its port: {switches.problem.message}</p>}
          {(switches.status === "error" || atSite?.length === 0) && <TypedPort onPick={setNamed} />}
          {switches.status === "ready" && switches.data.incomplete && <p className="muted">Not every switch at the site could be listed.</p>}
        </div>
      )}
      {m.problem && <ProblemBox problem={m.problem} />}
      <div className="actions">
        <button type="button" className="btn btn-primary" disabled={m.busy || !shown} onClick={() => void go()}>
          {shown ? `Power-cycle ${portWords(shown)} for ${device.name}` : `${a.button} ${device.name}`}
        </button>
        <button type="button" className="btn" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/** One switch at the site, drawn as its catalogue model so a port is pressed
 * rather than typed; typed where the model has no faceplate. */
function SwitchPick({ sw, named, onPick }: { sw: Device; named?: PortRef; onPick(p: PortRef): void }) {
  const { match, matchStatus, model, modelStatus } = useDeviceModel(sw.serial);
  const loading = matchStatus === "loading" || (match?.state === "recognised" && modelStatus === "loading");
  if (loading) return <Loading label={`Reading ${sw.name}'s ports`} />;
  const ports = model && model.ports.length > 0 ? facePorts(model) : undefined;
  if (!ports) return <TypedPort sw={sw} onPick={onPick} />;
  const selected = named?.switch_serial === sw.serial ? named.port : undefined;
  // The hardware's fact alone: which ports can carry PoE, from the model. Not
  // `poe`, which is a port delivering power now, and no intent, which this
  // panel does not read. (The catalogue page paints the same two marks; it is
  // a lazily loaded page, so its helper is not imported here.)
  const states: Record<string, PortState> = Object.fromEntries(model!.ports.map((p) => [p.name, { intent: "unread", ...(p.poe ? { poeCapable: true } : {}) }]));
  return (
    <div className="far-face">
      <p>{sw.name} <code>{sw.serial}</code></p>
      <div className="face-wrap">
        <DeviceFace ports={ports} states={states} selected={selected} onSelect={(port) => onPick({ switch_serial: sw.serial, port })}
          label={`${sw.name}, ${ports.length} ${plural(ports.length, "port")}`} photo={photoOf(model)} fit />
      </div>
    </div>
  );
}

/** The typed form: a port on a switch whose model has no faceplate, or a
 * switch and its port where the site's switches could not be listed. */
function TypedPort({ sw, onPick }: { sw?: Device; onPick(p: PortRef): void }) {
  const [serial, setSerial] = useState("");
  const [port, setPort] = useState("");
  const id = sw ? `poe-port-${sw.serial}` : "poe-port";
  const target = sw?.serial ?? serial.trim();
  const submit = (e: FormEvent) => { e.preventDefault(); onPick({ switch_serial: target, port: port.trim() }); };
  return (
    <form className="field" onSubmit={submit}>
      {sw ? <p>{sw.name} <code>{sw.serial}</code>: its model has no drawing, so type the port.</p> : (
        <><label htmlFor="poe-switch">Switch serial</label>
        <input id="poe-switch" autoComplete="off" value={serial} onChange={(e) => setSerial(e.target.value)} /></>
      )}
      {/* The console's 1/<n> is Ethernet<n-1> (#515, vendorNumber): the field takes Zenith's name. */}
      <label htmlFor={id}>Port, as <code>Ethernet&lt;n&gt;</code>: one less than the console&rsquo;s <code>1/&lt;n&gt;</code></label>
      <input id={id} autoComplete="off" value={port} onChange={(e) => setPort(e.target.value)} />
      <button type="submit" className="btn" disabled={!target || !port.trim()}>Show this port</button>
    </form>
  );
}
